import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Search } from "lucide-react";

const FILTERS = ["All", "Fundamentals", "Anterior", "Posterior", "Function"];

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data));
  }, []);

  const filtered = useMemo(() => {
    return courses.filter((c) => {
      const okCat = filter === "All" || c.category === filter;
      const okQ = !query || c.title.toLowerCase().includes(query.toLowerCase());
      return okCat && okQ;
    });
  }, [courses, filter, query]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="courses-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
        <div>
          <div className="font-mono-label text-primary">Full Curriculum</div>
          <h1 className="font-serif-display text-5xl md:text-6xl font-light mt-3 tracking-tighter leading-none">
            Courses & tracks
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl">
            Four progressive tracks — from instrument setup and additive wax-up to posterior morphology and functional occlusion.
          </p>
        </div>
        <div className="relative w-full lg:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search courses…"
            className="w-full pl-10 pr-3 py-3 bg-white border border-border text-sm outline-none focus:border-primary"
            style={{ borderRadius: 2 }}
            data-testid="courses-search"
          />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto mb-10 pb-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f.toLowerCase()}`}
            className={`font-mono-label px-4 py-2 border transition-colors whitespace-nowrap ${
              filter === f ? "bg-foreground text-background border-foreground" : "bg-transparent border-border hover:border-foreground"
            }`}
            style={{ borderRadius: 2 }}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((c) => (
          <Link
            to={`/courses/${c.id}`}
            key={c.id}
            className="porcelain-card overflow-hidden group relative"
            data-testid={`course-${c.slug}`}
          >
            {c.is_premium && (
              <div className="absolute top-4 right-4 z-10 font-mono-label bg-foreground text-background px-2 py-1" style={{ borderRadius: 2 }}>PRO</div>
            )}
            <img src={c.cover_image} alt={c.title} className="w-full h-52 object-cover" />
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="font-mono-label text-primary">{c.category}</div>
                <div className="font-mono-label text-muted-foreground">{c.level}</div>
              </div>
              <h3 className="font-serif-display text-3xl mt-3 leading-none tracking-tight">{c.title}</h3>
              <p className="text-sm text-muted-foreground mt-3">{c.subtitle}</p>
              <div className="mt-6 flex items-center justify-between text-sm">
                <span className="font-mono-label text-muted-foreground">{c.lesson_count} LESSONS · {Math.round(c.duration_minutes / 60)}H</span>
                <span className="group-hover:text-primary transition-colors flex items-center gap-1">
                  Enter <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 font-mono-label text-muted-foreground">No courses match your filter.</div>
      )}
    </div>
  );
}
